import React, { useCallback, useMemo, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import COMMON from "common";
import api from "services/api";
import sanitizeObject from "common/sanitize-object";
import { formatDate } from "common/calendar";
import { formatTimeValue } from "common/time";
import serveRequestErrors from "common/serve-request-errors";
import { NOTIFICATION_REFERENCE_PARAM } from "common/notification-routes";
import AppStatus from "components/app-status";
import AppEmptyState from "components/app-empty-state";
import AppSearchInput from "components/app-search-input";
import AppTable, { AppTableCell, AppTableEditIcon } from "components/app-table";
import AppOvertimeModal from "components/pages/overtime-management/app-overtime-modal";
import AppApplyOvertimeModal from "components/pages/overtime-management/app-apply-overtime-modal";
import AppCancelOvertimeModal from "components/pages/overtime-management/app-cancel-overtime-modal";
import addIcon from "assets/images/add-icon.svg";
import previewIcon from "assets/images/preview-icon.svg";

const PageOvertimePending = (props) => {
	const overtimeModalRef = useRef();
	const applyOvertimeRef = useRef();
	const cancelOvertimeRef = useRef();
	const [searchParams, setSearchParams] = useSearchParams();
	const memoSetSearchParams = useRef(setSearchParams);
	const paramsRef = useRef({
		page: parseInt(searchParams.get("page")) || 1,
		sort: searchParams.get("sort") || "",
	});
	const notificationRefId = useRef(searchParams.get(NOTIFICATION_REFERENCE_PARAM) || "");
	const [data, setData] = useState({ page: 0, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });
	const [loading, setLoading] = useState(true);
	const cancelRequest = useMemo(() => props.onHandleCancelRequest, [props.onHandleCancelRequest]);
	const isEmptyState = useMemo(() => !loading && data.items.length === 0, [loading, data.items]);

	const onHandleGetList = useCallback(async () => {
		let response = null;

		setLoading(true);
		setData((prev) => ({ ...prev, items: [] }));

		try {
			const payload = { ...paramsRef.current, size: 10 };

			memoSetSearchParams.current(sanitizeObject(payload), { replace: true });

			payload.page = paramsRef.current.page - 1;

			response = await api.get.overtime.pending(sanitizeObject(payload));
		} catch (error) {
			serveRequestErrors(error);
		}

		if (response) {
			setData((prev) => ({
				...prev,
				page: paramsRef.current.page,
				prev: !response.first,
				next: !response.last,
				items: response.content,
				total: response.totalPages,
				totalElements: response.totalElements,
				offset: response?.pageable?.offset || 0,
				status: response?.pageable,
			}));
		}

		setLoading(false);
	}, []);

	//prettier-ignore
	const onHandlePagination = useCallback((event) => {
		const control = event.currentTarget?.getAttribute("data-ctrl");

		if (control) {
			if (control === "prev") {
				if (paramsRef.current.page <= 1) return;
				paramsRef.current.page -= 1;
			} else {
				if (paramsRef.current.page >= data.total) return;
				paramsRef.current.page += 1;
			}
		} else {
			paramsRef.current.page = event.target.value;
		}

		onHandleGetList();
	}, [onHandleGetList, data.total]);

	const onHandleCreate = useCallback(() => {
		applyOvertimeRef.current.onHandleShow();
	}, []);

	/* cancel lives in the details modal, so the table only opens it */
	const onHandleViews = (id) => {
		overtimeModalRef.current.onHandleShow(id);
	};

	const OvertimeDateCell = useCallback(({ row }) => <AppTableCell left value={formatDate(row.original.overtimeDate)} />, []);

	const StartTimeCell = useCallback(({ row }) => <AppTableCell center value={formatTimeValue(row.original.startTime) || "-"} />, []);

	const EndTimeCell = useCallback(({ row }) => <AppTableCell center value={formatTimeValue(row.original.endTime) || "-"} />, []);

	const SubmitDateCell = useCallback(({ row }) => <AppTableCell center value={formatDate(row.original.createdDate)} />, []);

	const HoursCell = useCallback(({ row }) => <AppTableCell center value={row.original.overtimeHours ?? "-"} />, []);

	const StatusCell = useCallback(({ row }) => <AppStatus status={row.original.status} description={row.original.statusDescription} />, []);

	//prettier-ignore
	const ViewCell = useCallback(({ row }) => {
		return <AppTableEditIcon icon={previewIcon} onClick={() => onHandleViews(row.original.id)} />;
	}, []);

	//prettier-ignore
	const columns = useMemo(() => [
		{
			Header: "Overtime Date",
			accessor: "overtimeDate",
			disableSortBy: true,
			Cell: OvertimeDateCell,
		},
		{
			Header: "Start Time",
			accessor: "startTime",
			disableSortBy: true,
			Cell: StartTimeCell,
		},
		{
			Header: "End Time",
			accessor: "endTime",
			disableSortBy: true,
			Cell: EndTimeCell,
		},
		{
			Header: "Hours",
			accessor: "overtimeHours",
			disableSortBy: true,
			Cell: HoursCell,
		},
		{
			Header: "Submit Date",
			accessor: "createdDate",
			disableSortBy: true,
			Cell: SubmitDateCell,
		},
		{
			Header: "Status",
			accessor: "status",
			disableSortBy: true,
			Cell: StatusCell,
		},
		{
			Header: "",
			accessor: "*",
			disableSortBy: true,
			Cell: ViewCell,
		},
	], [OvertimeDateCell, StartTimeCell, EndTimeCell, HoursCell, SubmitDateCell, StatusCell, ViewCell]);

	useEffect(() => {
		onHandleGetList();
	}, [onHandleGetList]);

	// opens the details modal for notification
	useEffect(() => {
		const referenceId = searchParams.get(NOTIFICATION_REFERENCE_PARAM) || notificationRefId.current;

		if (!referenceId) return;

		notificationRefId.current = "";

		overtimeModalRef.current?.onHandleShow(referenceId);

		memoSetSearchParams.current(sanitizeObject({ ...paramsRef.current, size: 10 }), { replace: true });
	}, [searchParams]);

	useEffect(() => {
		return () => {
			cancelRequest(COMMON.ENDPOINT_PATH.OVERTIME.PENDING);
		};
	}, [cancelRequest]);

	return (
		<div className="page-overtime-pending">
			<div className="overtime-pending">
				<AppSearchInput buttons={[{ label: "New Overtime", icon: addIcon, onClick: onHandleCreate }]} />

				{isEmptyState && <AppEmptyState title="No records found" description="You don’t have any records yet" disabledButton />}
				{!isEmptyState && <AppTable columns={columns} pages={data} onHandlePagination={onHandlePagination} />}
			</div>

			<AppOvertimeModal ref={overtimeModalRef} cancelRef={cancelOvertimeRef} />

			<AppApplyOvertimeModal ref={applyOvertimeRef} onHandleGetList={onHandleGetList} />

			<AppCancelOvertimeModal ref={cancelOvertimeRef} onHandleGetList={onHandleGetList} />
		</div>
	);
};

export default PageOvertimePending;
